import { FiLink } from 'react-icons/fi';

export const conjunctionsBasicData = {
  courseId: 'conjunctions-basic',
  courseType: 'grammar',
  title: 'Conjunctions (and, but, or, so)',
  icon: FiLink,
  overview: {
    impact: 'Connect ideas for better coherence and higher band scores',
    focus: 'Coordinating conjunctions and basic subordinating conjunctions',
    uses: 'Writing Tasks 1 & 2, Speaking fluency',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Coordinating Conjunctions (FANBOYS)',
      content: `<p>Seven coordinating conjunctions join equal parts: <strong>For, And, Nor, But, Or, Yet, So</strong></p>`,
      table: {
        headers: [
          'Conjunction',
          'Function',
          'Example'
        ],
        rows: [
          [
            'and',
            'addition',
            'I like tea and coffee.'
          ],
          [
            'but',
            'contrast',
            'She is smart but lazy.'
          ],
          [
            'or',
            'choice',
            'Do you want tea or coffee?'
          ],
          [
            'so',
            'result',
            'It was cold, so I wore a jacket.'
          ],
          [
            'yet',
            'contrast (formal)',
            'He is rich, yet unhappy.'
          ],
          [
            'for',
            'reason (formal)',
            'She left, for she was tired.'
          ]
        ]
      }
    },
    {
      title: 'Common Subordinating Conjunctions',
      content: `<p>Join a dependent clause to a main clause:</p>`,
      points: [
        'because/since/as (reason): \'I stayed home because it was raining.\'',
        'although/even though (contrast): \'Although it rained, we went out.\'',
        'if/unless (condition): \'If you study hard, you will pass.\'',
        'when/while/before/after (time): \'I\'ll call you when I arrive.\''
      ]
    },
    {
      title: 'Using Conjunctions in IELTS Writing',
      content: `<p>Good conjunction use boosts your Coherence & Cohesion score:</p>`,
      points: [
        'Vary your conjunctions — don\'t always use \'and\' and \'but\'',
        'Place a comma before coordinating conjunctions joining two clauses',
        'Start sentences with subordinating conjunctions for variety',
        '\'However\' is NOT a conjunction — it\'s an adverb (needs semicolon or new sentence)'
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Right Conjunction',
      type: 'select',
      instructions: 'Select the best conjunction.',
      questions: [
        {
          text: 'I wanted to go out, ___ it was raining.',
          options: [
            'but',
            'and',
            'so'
          ],
          answer: 'but',
          explanation: 'Contrast between wanting to go out and rain → \'but\'.'
        },
        {
          text: 'She studied hard, ___ she passed the exam.',
          options: [
            'so',
            'but',
            'or'
          ],
          answer: 'so',
          explanation: 'Result of studying → \'so\'.'
        },
        {
          text: '___ it was late, we decided to continue working.',
          options: [
            'Although',
            'Because',
            'So'
          ],
          answer: 'Although',
          explanation: 'Contrast: late but continued → \'Although\'.'
        },
        {
          text: 'I\'ll bring an umbrella ___ it rains.',
          options: [
            'in case',
            'so',
            'and'
          ],
          answer: 'in case',
          explanation: 'Precaution for possible rain → \'in case\'.'
        }
      ]
    },
    {
      title: 'Coordinating Conjunctions',
      type: 'select',
      instructions: 'Choose the correct coordinating conjunction (and, but, or, so, yet).',
      questions: [
        {
          text: 'I like both swimming ___ running.',
          options: [
            'and',
            'but',
            'or'
          ],
          answer: 'and',
          explanation: '\'Both...and\' is a fixed pair expressing addition.'
        },
        {
          text: 'He wants to travel, ___ he has no money.',
          options: [
            'but',
            'and',
            'so'
          ],
          answer: 'but',
          explanation: 'Contrast between desire and lack of money → \'but\'.'
        },
        {
          text: 'Would you like to sit inside ___ outside?',
          options: [
            'or',
            'and',
            'yet'
          ],
          answer: 'or',
          explanation: 'Offering a choice between two options → \'or\'.'
        },
        {
          text: 'The road was blocked, ___ we took a different route.',
          options: [
            'so',
            'but',
            'and'
          ],
          answer: 'so',
          explanation: 'The blocked road caused the result of changing route → \'so\'.'
        }
      ]
    },
    {
      title: 'Subordinating Conjunctions',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct subordinating conjunction (because, although, while, if).',
      questions: [
        {
          text: 'She passed the exam ___ she studied very hard.',
          answer: 'because',
          explanation: '\'Because\' gives the reason for passing the exam.'
        },
        {
          text: '___ the weather was terrible, we enjoyed the trip.',
          answer: 'Although',
          explanation: '\'Although\' introduces a contrast — bad weather, but still enjoyed.'
        },
        {
          text: 'I was reading ___ my brother was watching TV.',
          answer: 'while',
          explanation: '\'While\' indicates two actions happening at the same time.'
        },
        {
          text: 'You will improve ___ you practice every day.',
          answer: 'if',
          explanation: '\'If\' introduces a condition for the result (improving).'
        }
      ]
    },
    {
      title: 'Combine the Sentences',
      type: 'writing',
      instructions: 'Rewrite each pair of sentences as a single sentence using a conjunction.',
      questions: [
        {
          text: 'It was very cold outside. We decided to stay home.',
          sampleAnswer: 'It was very cold outside, so we decided to stay home.',
          explanation: '\'So\' shows result, or use \'because\': \'We decided to stay home because it was very cold outside.\''
        },
        {
          text: 'She is very talented. She doesn\'t practise enough.',
          sampleAnswer: 'She is very talented, but she doesn\'t practise enough.',
          explanation: '\'But\' or \'although\' shows contrast: \'Although she is very talented, she doesn\'t practise enough.\''
        },
        {
          text: 'You can take the bus. You can walk to school.',
          sampleAnswer: 'You can take the bus or walk to school.',
          explanation: '\'Or\' presents a choice between two options.'
        }
      ]
    }
  ],
  tips: [
    'FANBOYS = For, And, Nor, But, Or, Yet, So',
    'Put a comma before FANBOYS when joining two full sentences',
    '\'Although\' at the start needs NO \'but\' later (common mistake)',
    'Vary conjunctions in Writing to boost coherence scores'
  ],
  dailyPractice: [
    'Practice conjunctions (and, but, or, so) concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Basic Conjunctions: FANBOYS for coordination, because/although/if/when for subordination. Key for IELTS Writing coherence and Speaking fluency.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...conjunctionsBasicData,
      title: conjunctionsBasicData.title + ' (Foundations)',
      overview: { ...conjunctionsBasicData.overview, duration: '15-20 minutes' },
      sections: conjunctionsBasicData.sections.slice(0, Math.max(2, Math.ceil(conjunctionsBasicData.sections.length / 2))),
      exercises: conjunctionsBasicData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...conjunctionsBasicData,
      title: conjunctionsBasicData.title + ' (Advanced)',
      overview: { ...conjunctionsBasicData.overview, duration: '30-35 minutes' },
      sections: conjunctionsBasicData.sections,
      exercises: conjunctionsBasicData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(conjunctionsBasicData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return conjunctionsBasicData;
}
